import { useState, useEffect, useMemo } from 'react'
import { FaTrashAlt, FaPencilAlt, FaTshirt } from 'react-icons/fa'
import { FiDollarSign, FiAlertTriangle } from 'react-icons/fi'
import { HiOutlineLink } from 'react-icons/hi2'
import api from '../../../utils/api'
import Select from 'react-select'
import SelectStyles from '../../../shared/css/SelectStyle'
import Input from '../../../shared/components/form/input'
import Button from '../../../shared/components/Button/Button'
import Modal from '../../../shared/modal/Modal'
import PlusSinal from '../../../shared/components/PlusSinal/PlusSinal'
import FormLayout from '../../../shared/components/FormLayout/FormLayout'
import ConfirmDeleteModal from '../../../shared/components/ConfirmDeleteModal/ConfirmDeleteModal'
import useFlashMessage from '../../../hooks/useFlashMessage'
import NumberToMoney from '../../../shared/functions/NumberToMoney'
import getCostForCloth from '../../../shared/functions/getCostForCloth'
import getRefForCloth from '../../../shared/functions/getRefForCloth'
import Cost from '../Cost/Cost'
import EditColorIdentity from '../EditColorIdentity'
import AssociateColorsToCloth from './AssociateColorsToCloth'
import '../../../shared/css/subItemList.css'
import './ProviderColorCatalog.css'

const NO_COLOR_TYPE = { _id: null, label: 'Sem tipo', value: null }

function ProviderColorCatalog({ provider, colorTypes }) {
    const colorTypeOptions = [NO_COLOR_TYPE, ...colorTypes]
    const [token] = useState(localStorage.getItem('token') || '')
    const [catalog, setCatalog] = useState([])
    const [load, setLoad] = useState(false)
    const [search, setSearch] = useState('')

    // Adicionar Cor (lote)
    const [modalAdd, setModalAdd] = useState(false)
    const [colorType, setColorType] = useState(NO_COLOR_TYPE)
    const [items, setItems] = useState([])
    const [modalAddItem, setModalAddItem] = useState(false)
    const [nameDraft, setNameDraft] = useState('')
    const [refDraft, setRefDraft] = useState('')
    const [editingDraftIndex, setEditingDraftIndex] = useState(null)
    const [modalDeleteDraftItem, setModalDeleteDraftItem] = useState(false)
    const [deleteDraftIndex, setDeleteDraftIndex] = useState(null)
    const [modalSkipped, setModalSkipped] = useState(false)
    const [skippedItems, setSkippedItems] = useState([])

    // Editar / apagar cor existente
    const [modalEditColor, setModalEditColor] = useState(false)
    const [editColorId, setEditColorId] = useState(null)
    const [editColorClothId, setEditColorClothId] = useState(null)
    const [editColorClothName, setEditColorClothName] = useState('')
    const [modalDelete, setModalDelete] = useState(false)
    const [deleteId, setDeleteId] = useState(null)
    const [deleteName, setDeleteName] = useState('')
    const [deleteClothId, setDeleteClothId] = useState(null)
    const [deleteClothName, setDeleteClothName] = useState('')

    // Valores (custo) e Associar Cor ao Tecido
    const [modalCost, setModalCost] = useState(false)
    const [modalAssociateCloth, setModalAssociateCloth] = useState(false)

    const { setFlashMessage } = useFlashMessage()

    const getAll = () => {
        api.get('/color/getall', {
            params: { provider: provider._id },
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            if (response.data) {
                setCatalog(response.data.colors)
                setLoad(true)
            }
        })
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider._id])

    function openAddModal() {
        setColorType(NO_COLOR_TYPE)
        setItems([])
        setModalAdd(true)
    }

    function openAddItemModal() {
        setNameDraft('')
        setRefDraft('')
        setEditingDraftIndex(null)
        setModalAddItem(true)
    }

    function openEditDraftItem(idx) {
        setNameDraft(items[idx].name)
        setRefDraft(items[idx].ref)
        setEditingDraftIndex(idx)
        setModalAddItem(true)
    }

    function submitDraftItem(e) {
        e.preventDefault()
        const name = nameDraft.trim()
        if (!name) {
            setFlashMessage('Informe o nome da cor', 'error')
            return
        }
        const entry = { name, ref: refDraft.trim() }
        if (editingDraftIndex !== null) {
            setItems((prev) => prev.map((it, i) => (i === editingDraftIndex ? entry : it)))
        } else {
            setItems((prev) => [...prev, entry])
        }
        setModalAddItem(false)
    }

    function askRemoveDraftItem(idx) {
        setDeleteDraftIndex(idx)
        setModalDeleteDraftItem(true)
    }

    function confirmRemoveDraftItem() {
        setItems((prev) => prev.filter((_, i) => i !== deleteDraftIndex))
        setModalDeleteDraftItem(false)
    }

    async function submit(e) {
        e.preventDefault()

        if (!items.length) {
            setFlashMessage('Informe ao menos um nome de cor', 'error')
            return
        }

        const colorTypeToSend = colorType && colorType._id ? colorType : null

        let msgType = 'success'
        const data = await api.post('/color/new-batch', {
            provider: provider._id,
            colorType: colorTypeToSend,
            items,
        }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data)
            .catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)

        if (msgType !== 'error') {
            setModalAdd(false)
            getAll()
            if (data.skipped && data.skipped.length > 0) {
                setSkippedItems(data.skipped)
                setModalSkipped(true)
            }
        }
    }

    const editSuccess = () => {
        setModalEditColor(false)
        getAll()
    }

    async function confirmDelete() {
        let msgType = 'success'
        // Card "Sem tecido" (deleteClothId null): não há tecido pra desassociar,
        // apaga o cadastro da cor inteiro pra esse fornecedor. Card de um tecido
        // real: só tira a cor daquele tecido (bulk-remove-cloth com 1 id), a cor
        // continua existindo nos outros tecidos.
        const data = deleteClothId
            ? await api.post('/color/bulk-remove-cloth', { cloth: deleteClothId, colorIds: [deleteId] }, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` },
            }).then((response) => response.data)
                .catch((err) => { msgType = 'error'; return err.response.data })
            : await api.delete(`/color/delete/${deleteId}`, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` },
            }).then((response) => response.data)
                .catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        setModalDelete(false)
        if (msgType !== 'error') getAll()
    }

    const searchLowerCase = search.trim().toLowerCase()
    const filteredCatalog = !searchLowerCase ? catalog : catalog.filter((item) =>
        item.name?.toLowerCase().includes(searchLowerCase) ||
        item.ref?.toLowerCase().includes(searchLowerCase) ||
        item.colorType?.name?.toLowerCase().includes(searchLowerCase)
    )

    // Uma cor pode existir em vários tecidos — entra em cada grupo de tecido a que pertence.
    const groups = useMemo(() => {
        const list = []
        const indexByCloth = {}
        filteredCatalog.forEach((item) => {
            const itemCloths = item.cloths && item.cloths.length > 0 ? item.cloths : [{ _id: 'sem-tecido', name: 'Sem tecido' }]
            itemCloths.forEach((cloth) => {
                const key = cloth._id
                if (indexByCloth[key] === undefined) {
                    indexByCloth[key] = list.length
                    list.push({ clothId: key, clothName: cloth.name, items: [] })
                }
                list[indexByCloth[key]].items.push(item)
            })
        })
        list.forEach((g) => g.items.sort((a, b) => a.name.localeCompare(b.name)))
        list.sort((a, b) => a.clothName.localeCompare(b.clothName))
        return list
    }, [filteredCatalog])

    return (
        <div className="pcc-wrap">
            <div className="pcc-header">
                <p className="fl-title">Catálogo de {provider.name}</p>
                <span className="fl-title-accent" />
            </div>

            <div className="pcc-actions">
                <div className="pcc-actions-left">
                    <Button variant="new" onClick={openAddModal}>Adicionar Cor</Button>
                    <Button variant="muted" icon={HiOutlineLink} onClick={() => setModalAssociateCloth(true)}>Associar Tecido</Button>
                    <Button variant="muted" icon={FiDollarSign} onClick={() => setModalCost(true)}>Adicionar Valores</Button>
                </div>
                <div className="pg-search">
                    <Input
                        type="text"
                        name="search"
                        placeholder="Buscar por nome, ref ou tipo..."
                        value={search}
                        handleOnChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="pcc-list">
                {!load ? (
                    <p className="pg-loading">Carregando catálogo...</p>
                ) : filteredCatalog.length === 0 ? (
                    <p className="sil-empty">
                        {catalog.length === 0 ? 'Nenhuma cor cadastrada ainda para esse fornecedor.' : 'Nenhuma cor encontrada para essa busca.'}
                    </p>
                ) : groups.map((group) => {
                    const clothIdForLookup = group.clothId === 'sem-tecido' ? null : group.clothId
                    return (
                        <div key={group.clothId} className="pcc-cloth-group">
                            <p className="pcc-cloth-title"><FaTshirt size={13} /> {group.clothName}</p>
                            {group.items.map((item) => {
                                const costs = getCostForCloth(item, clothIdForLookup)
                                const effectiveRef = getRefForCloth(item, clothIdForLookup)
                                return (
                                    <div key={item._id} className="pcc-row">
                                        <div className="pcc-row-main">
                                            <span className="pcc-row-name">{item.name}</span>
                                            {item.colorType?.name && <span className="pcc-row-type">{item.colorType.name}</span>}
                                            {effectiveRef && <span className="pcc-row-ref">ref: {effectiveRef}</span>}
                                            <FaPencilAlt
                                                className="shd-icon-row-edit"
                                                title="Editar"
                                                onClick={() => {
                                                    setEditColorId(item._id)
                                                    setEditColorClothId(clothIdForLookup)
                                                    setEditColorClothName(group.clothName)
                                                    setModalEditColor(true)
                                                }}
                                            />
                                            <FaTrashAlt
                                                className="shd-icon-row-trash"
                                                title="Apagar"
                                                onClick={() => {
                                                    setDeleteId(item._id)
                                                    setDeleteName(item.name)
                                                    setDeleteClothId(clothIdForLookup)
                                                    setDeleteClothName(group.clothName)
                                                    setModalDelete(true)
                                                }}
                                            />
                                        </div>
                                        <div className="pcc-row-tags">
                                            {costs.length > 0 ? (
                                                costs.map((c) => (
                                                    <span className="pcc-cost-tag" key={c._id}>
                                                        {c.description && `${c.description} — `}
                                                        {c.minUnit}–{c.maxUnit} un: R$ {NumberToMoney(c.cost)}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="pcc-no-cost-tag">
                                                    <FiAlertTriangle size={12} />
                                                    Sem custo definido
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )
                })}
            </div>

            <Modal
                modalStatus={modalAdd}
                changeStatus={setModalAdd}
                w={70}
                t={3}
                h={90}
                content={
                    <FormLayout
                        title="Adicionar Cor"
                        onSubmit={submit}
                        actions={
                            <>
                                <Button variant="save" type="submit">Salvar</Button>
                                <Button variant="cancel" type="button" onClick={() => setModalAdd(false)}>Cancelar</Button>
                            </>
                        }
                    >
                        <div className="form-group">
                            <label className="input-label">Tipo de Cor:</label>
                            <Select
                                options={colorTypeOptions}
                                styles={SelectStyles}
                                onChange={setColorType}
                                value={colorType}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                        <div className="form-group">
                            <label className="input-label">Cores:
                                <PlusSinal onClick={openAddItemModal} title="Adicionar cor" />
                            </label>
                            <div className="sil-list">
                                {items.length === 0 ? (
                                    <p className="sil-empty">Nenhuma cor adicionada ainda.</p>
                                ) : items.map((it, idx) => (
                                    <div key={`${it.name}-${idx}`} className="sil-row pcc-draft-row">
                                        <span className="sil-label">{it.name}</span>
                                        {it.ref && <span className="sil-value">ref: {it.ref}</span>}
                                        <FaPencilAlt
                                            className="shd-icon-row-edit"
                                            title="Editar"
                                            onClick={() => openEditDraftItem(idx)}
                                        />
                                        <FaTrashAlt
                                            className="shd-icon-row-trash"
                                            title="Remover"
                                            onClick={() => askRemoveDraftItem(idx)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </FormLayout>
                }
            />

            <Modal
                modalStatus={modalAddItem}
                changeStatus={setModalAddItem}
                w={35}
                content={
                    <FormLayout
                        title={editingDraftIndex !== null ? 'Editar Cor' : 'Adicionar Cor'}
                        onSubmit={submitDraftItem}
                        actions={
                            <>
                                <Button variant="save" type="submit">Salvar</Button>
                                <Button variant="cancel" type="button" onClick={() => setModalAddItem(false)}>Cancelar</Button>
                            </>
                        }
                    >
                        <div className="form-group">
                            <Input
                                text="Nome da cor"
                                type="text"
                                name="nameDraft"
                                value={nameDraft}
                                handleOnChange={(e) => setNameDraft(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <Input
                                text="Referência (opcional)"
                                type="text"
                                name="refDraft"
                                value={refDraft}
                                handleOnChange={(e) => setRefDraft(e.target.value)}
                            />
                        </div>
                    </FormLayout>
                }
            />

            <Modal
                modalStatus={modalSkipped}
                changeStatus={setModalSkipped}
                w={45}
                content={
                    <FormLayout
                        title="Cores já existentes"
                        actions={
                            <Button variant="cancel" type="button" onClick={() => setModalSkipped(false)}>Fechar</Button>
                        }
                    >
                        <p className="ccm-skipped-hint">
                            {skippedItems.length} {skippedItems.length === 1 ? 'nome já existia' : 'nomes já existiam'} nesse fornecedor/tipo e não {skippedItems.length === 1 ? 'foi criado' : 'foram criados'} de novo:
                        </p>
                        <div className="sil-list">
                            {skippedItems.map((item, idx) => (
                                <div key={`${item.name}-${idx}`} className="sil-row">
                                    <span className="sil-label">{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </FormLayout>
                }
            />

            <Modal
                modalStatus={modalEditColor}
                changeStatus={setModalEditColor}
                w={45}
                content={
                    editColorId && <EditColorIdentity id={editColorId} clothId={editColorClothId} clothName={editColorClothName} handleOnSuccess={editSuccess} btCancelAction={() => setModalEditColor(false)} />
                }
            />

            <Modal
                modalStatus={modalCost}
                changeStatus={() => { setModalCost(false); getAll() }}
                w={92}
                t={3}
                h={90}
                content={
                    <Cost providerId={provider._id} btCancelAction={() => { setModalCost(false); getAll() }} />
                }
            />

            <Modal
                modalStatus={modalAssociateCloth}
                changeStatus={() => { setModalAssociateCloth(false); getAll() }}
                w={92}
                t={3}
                h={90}
                content={
                    <AssociateColorsToCloth providerId={provider._id} btCancelAction={() => { setModalAssociateCloth(false); getAll() }} handleOnSuccess={() => { setModalAssociateCloth(false); getAll() }} />
                }
            />

            <ConfirmDeleteModal
                modalStatus={modalDelete}
                changeStatus={setModalDelete}
                onConfirm={confirmDelete}
                message={deleteClothId
                    ? `Deseja realmente remover essa cor do tecido "${deleteClothName}"? A cor continua existindo nos outros tecidos e nesse fornecedor.`
                    : 'Deseja realmente excluir essa cor desse fornecedor?'}
                itemName={deleteName}
            />

            <ConfirmDeleteModal
                modalStatus={modalDeleteDraftItem}
                changeStatus={setModalDeleteDraftItem}
                onConfirm={confirmRemoveDraftItem}
                message="Deseja realmente remover essa cor da lista?"
                itemName={deleteDraftIndex !== null ? items[deleteDraftIndex]?.name : ''}
            />
        </div>
    )
}

export default ProviderColorCatalog
